import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { thisWeekHighlights } from '../../../mocks/thisWeek';
import { getCurrentWeekRange, getRelativeDateFormatted, getWeekdayShort } from '../../../utils/dateHelpers';

const typeIcons: Record<string, string> = {
  event: 'ri-calendar-event-line',
  deal: 'ri-price-tag-3-line',
  opening: 'ri-store-2-line',
  news: 'ri-newspaper-line',
};

const typeLabels: Record<string, string> = {
  event: 'Event',
  deal: 'Deal',
  opening: 'New Opening',
  news: 'News',
};

export default function ThisWeekHighlights() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const weekRange = useMemo(() => getCurrentWeekRange(), []);

  // Compute dynamic dates and days for each highlight
  const highlightsWithDynamicDates = useMemo(() => {
    return thisWeekHighlights.map((item, index) => ({
      ...item,
      date: getRelativeDateFormatted(index),
      day: getWeekdayShort(index)
    }));
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  const featured = highlightsWithDynamicDates[activeIndex];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-full mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
              </span>
              <span className="text-rose-700 text-sm font-semibold tracking-wide uppercase">This Week · {weekRange}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              What&apos;s Happening <span className="text-teal-600">This Week</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-xl">
              Don&apos;t miss out on the latest events, deals, openings, and community updates in Roseville
            </p>
          </div>
          <Link
            to="/calendar"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer self-start md:self-auto"
          >
            <i className="ri-calendar-line"></i>
            View Full Calendar
          </Link>
        </div>

        {/* Featured Highlight */}
        <div className="mb-10">
          <Link to={featured.link} className="block cursor-pointer group">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-[400px]">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

              <div className="absolute top-5 left-5 flex items-center gap-2">
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${featured.badgeColor}`}>
                  {featured.badge}
                </span>
                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/20 text-white backdrop-blur-sm">
                  <i className={`${typeIcons[featured.type]} mr-1`}></i>
                  {typeLabels[featured.type]}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-4 text-white/80 text-sm mb-3">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {featured.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {featured.time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-line"></i>
                    {featured.location}
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-teal-300 transition-colors">
                  {featured.title}
                </h3>
                <p className="text-white/85 text-base max-w-2xl leading-relaxed">
                  {featured.description}
                </p>
              </div>
            </div>
          </Link>
        </div>

        {/* Scrollable Cards */}
        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg text-gray-700"></i>
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg text-gray-700"></i>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {highlightsWithDynamicDates.map((item, idx) => (
              <Link
                key={item.id}
                to={item.link}
                onClick={() => setActiveIndex(idx)}
                className={`flex-shrink-0 w-[300px] rounded-xl overflow-hidden border transition-all duration-300 cursor-pointer group ${
                  activeIndex === idx
                    ? 'border-teal-300 shadow-lg ring-1 ring-teal-200'
                    : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
                }`}
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <div className="bg-white rounded-lg px-2.5 py-1.5 text-center shadow-md">
                      <div className="text-teal-600 font-bold text-[10px] uppercase">{item.day}</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${typeIcons[item.type]} text-teal-500 text-sm`}></i>
                    <span className="text-xs text-gray-500 font-medium">{typeLabels[item.type]}</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1 group-hover:text-teal-600 transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-3">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {item.time}
                    </span>
                    <span className="flex items-center gap-1 truncate">
                      <i className="ri-map-pin-line"></i>
                      {item.location}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {highlightsWithDynamicDates.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                idx === activeIndex ? 'w-7 h-2 bg-teal-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}